import { Component, OnInit } from "@angular/core";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { AuthenticationService } from "src/app/core/services/auth.service";
import Swal from "sweetalert2";

@Component({
  selector: "session-timeout",
  template: "",
})
export class SessionTimeoutComponent implements OnInit {
  /** Inactivity before the warning popup appears. */
  idleTimeInMins = 60;
  /** Countdown shown in the warning popup (display only; no auto-logout on expiry). */
  idleTimeoutInMins = 5;
  /** Max session length since sign-in; popup auto-signs-out when this is reached. */
  maxSessionHours = 12;
  /** Refresh tokens while the warning popup stays open. */
  tokenRefreshDuringWarningMs = 5 * 60 * 1000;

  private idleLogoutInProgress = false;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private warningMaintenanceIntervalId: ReturnType<typeof setInterval> | null =
    null;

  constructor(
    private idle: Idle,
    private readonly _authService: AuthenticationService
  ) {
    idle.setIdle(this.idleTimeInMins * 60);
    idle.setTimeout(false);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleStart.subscribe(() => {
      if (this.isMaxSessionDurationExceeded()) {
        this.showSessionExpiredLogout();
        return;
      }
      this.showIdleWarning();
    });
  }

  ngOnInit(): void {
    this._authService.ensureSessionStartedAt();
    // TEMP: @ng-idle watch() uses setInterval(1s); disabled for Engage perf investigation.
    // this.idle.watch();
  }

  private showSessionExpiredLogout(): void {
    this.clearCountdownInterval();
    this.clearWarningMaintenanceInterval();
    this.idle.stop();
    this.idleLogoutInProgress = false;
    void Swal.close();

    Swal.fire({
      title: "Session expired",
      html: `<p class="idle-warning-text">Your session has been active for ${this.maxSessionHours} hours since sign-in. You will be signed out.</p>`,
      icon: "warning",
      iconHtml: '<i class="fa-solid fa-circle-exclamation"></i>',
      customClass: {
        popup: "idle-warning-popup",
        icon: "idle-warning-icon",
      },
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      backdrop: "rgb(230 230 230 / 88%)",
      didOpen: () => {
        setTimeout(() => this.completeIdleLogout(), 2000);
      },
    });
  }

  private showIdleWarning(): void {
    this.idle.stop();
    this.idleLogoutInProgress = false;

    Swal.fire({
      title: "Are you still there?",
      html: `<p class="idle-warning-text">You will be logged out in <b class="idle-warning-timer">${this.idleTimeoutInMins} mins</b>!</p>`,
      icon: "warning",
      iconHtml: '<i class="fa-solid fa-circle-exclamation"></i>',
      customClass: {
        popup: "idle-warning-popup",
        icon: "idle-warning-icon",
        confirmButton: "btn-idle-confirm",
        cancelButton: "btn-idle-cancel",
      },
      allowEscapeKey: false,
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonText: "No",
      cancelButtonText: "Yes",
      reverseButtons: true,
      backdrop: "rgb(230 230 230 / 88%)",
      timer: this.idleTimeoutInMins * 60000,
      didOpen: () => {
        void this._authService.refreshSession("idle-warning");
        // const timer = Swal.getPopup()?.querySelector(".idle-warning-timer");
        this.clearCountdownInterval();
        // TEMP: disabled for Engage perf investigation.
        // this.countdownIntervalId = setInterval(() => {
        //   const remainingMs = Swal.getTimerLeft();
        //   if (timer && remainingMs != null) {
        //     timer.textContent = this.secondsToMinsSecsText(remainingMs);
        //   }
        //   if (remainingMs != null && remainingMs <= 1000) {
        //     Swal.increaseTimer(this.idleTimeoutInMins * 60000);
        //   }
        // }, 1000);
        this.clearWarningMaintenanceInterval();
        this.warningMaintenanceIntervalId = setInterval(() => {
          if (this.isMaxSessionDurationExceeded()) {
            this.showSessionExpiredLogout();
            return;
          }
          void this._authService.refreshSession("idle-warning");
        }, this.tokenRefreshDuringWarningMs);
      },
      willClose: () => {
        this.clearCountdownInterval();
        this.clearWarningMaintenanceInterval();
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.completeIdleLogout();
      } else if (!this.idleLogoutInProgress) {
        this.idle.watch();
      }
    });
  }

  private isMaxSessionDurationExceeded(): boolean {
    const durationMs = this._authService.getSessionDurationMs();
    if (durationMs == null) {
      return false;
    }
    return durationMs >= this.maxSessionHours * 60 * 60 * 1000;
  }

  /** Close the warning dialog, clear session, and redirect to auth login. */
  private completeIdleLogout(): void {
    if (this.idleLogoutInProgress) {
      return;
    }
    this.idleLogoutInProgress = true;
    this.clearCountdownInterval();
    this.clearWarningMaintenanceInterval();
    this.idle.stop();
    void Swal.close();
    this._authService.logout();
  }

  private clearWarningMaintenanceInterval(): void {
    if (this.warningMaintenanceIntervalId != null) {
      clearInterval(this.warningMaintenanceIntervalId);
      this.warningMaintenanceIntervalId = null;
    }
  }

  private clearCountdownInterval(): void {
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  private secondsToMinsSecsText = (milliseconds: number): string => {
    const seconds = milliseconds / 1000;
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);
    const mDisplay =
      m > 0 ? m + (m == 1 ? " min" : " mins") + (s > 0 ? ", " : "") : "";
    const sDisplay = s > 0 ? s + (s == 1 ? " sec" : " secs") : "";
    return mDisplay + sDisplay;
  };
}
